import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { useEffect, useMemo, useState } from "react";
import {
  CommonDrawerStyle,
  DrawerForm,
  DrawerHeader,
} from "../Common/styles/drawer";
import { InputWrapper, LabelStyle } from "../Common/styles/form";
import { ModalActionButtonStyles } from "../Common/styles/modal";
import { patchRegistryFilters } from "../../Redux/reducers/aiSearchSlice";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import SingleSelectFilterField from "../Common/UI/participantFilterFields/SingleSelectFilterField";
import TextFilterField from "../Common/UI/participantFilterFields/TextFilterField";
import TagsAutocompleteFilterField from "../Common/UI/participantFilterFields/TagsAutocompleteFilterField";
import {
  REGISTRY_DRAWER_FILTER_KEYS,
  buildRegistryDrawerFilterState,
  formatRegistryTagIds,
  parseRegistryTagIds,
} from "../../utils/participantDrawerFilters";
import { RegistryFilterMeta } from "../../utils/registryColumnFilters";

type Props = {
  closeModal: () => void;
  // Loaded once by the participant list (useRegistryFilterOptions) and reused
  // here, so opening the drawer does not refetch languages / filters / tags.
  filterMeta: Record<string, RegistryFilterMeta>;
  tagList: { id: string; label: string }[];
  loading: boolean;
};

const GlobalFilterModal = ({
  closeModal,
  filterMeta,
  tagList,
  loading,
}: Props) => {
  const dispatch = useAppDispatch();
  const filterOptions = useMemo(
    () =>
      Object.values(filterMeta).filter((item) =>
        ["select", "input", "date"].includes(item.type),
      ),
    [filterMeta],
  );
  const sliceFilters = useAppSelector((state) => state.aiSearch.filters);
  const [filterState, setFilterState] = useState<Record<string, string>>({});
  const [dateError, setDateError] = useState("");

  useEffect(() => {
    setFilterState(buildRegistryDrawerFilterState(sliceFilters));
  }, [sliceFilters]);

  const handleFilterChange = (filterName: string, value: string) => {
    setFilterState((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    if (
      filterName === "screenerCompletedFrom" ||
      filterName === "screenerCompletedTo"
    ) {
      setDateError("");
    }
  };

  const handleClearAll = () => {
    const cleared: Record<string, string> = {};
    REGISTRY_DRAWER_FILTER_KEYS.forEach((key) => {
      cleared[key] = "";
    });
    setFilterState(cleared);
    setDateError("");
  };

  const submit = () => {
    const from = filterState.screenerCompletedFrom;
    const to = filterState.screenerCompletedTo;
    if (from && to && from > to) {
      setDateError(
        "Screener Completion Date To must be on or after Screener Completion Date From",
      );
      return;
    }
    setDateError("");
    const patch: Record<string, string> = {};
    REGISTRY_DRAWER_FILTER_KEYS.forEach((key) => {
      patch[key] = filterState[key] || "";
    });
    dispatch(patchRegistryFilters(patch));
    closeModal();
  };

  return (
    <Box sx={CommonDrawerStyle}>
      <DrawerHeader closeDrawer={closeModal} title="Filter By" />
      {!loading ? (
        <Box sx={DrawerForm}>
          <Box sx={{ overflow: "auto", pr: 0.5 }}>
            {filterOptions.map((item) => {
              if (item.type === "input") {
                return (
                  <Box key={item.filter} sx={{ mb: 2 }}>
                    <TextFilterField
                      label={item.label || item.filter}
                      value={filterState[item.filter] || ""}
                      onChange={(v) => handleFilterChange(item.filter, v)}
                    />
                  </Box>
                );
              }
              if (item.type === "select") {
                return (
                  <Box key={item.filter} sx={{ mb: 2 }}>
                    <SingleSelectFilterField
                      label={item.label || item.filter}
                      value={filterState[item.filter] || ""}
                      onChange={(v) => handleFilterChange(item.filter, v)}
                      options={item.options || []}
                    />
                  </Box>
                );
              }
              if (item.type === "date") {
                return (
                  <LocalizationProvider
                    key={item.filter}
                    dateAdapter={AdapterLuxon}
                  >
                    <FormControl sx={InputWrapper}>
                      <FormLabel sx={LabelStyle}>
                        {item.label || item.filter}
                      </FormLabel>
                      <DatePicker
                        maxDate={DateTime.now()}
                        value={
                          filterState[item.filter]
                            ? DateTime.fromISO(filterState[item.filter])
                            : null
                        }
                        onChange={(value) =>
                          handleFilterChange(
                            item.filter,
                            value ? value.toISODate() || "" : "",
                          )
                        }
                        slotProps={{
                          field: {
                            clearable: true,
                            onClear: () => handleFilterChange(item.filter, ""),
                          },
                        }}
                      />
                      <FormHelperText> </FormHelperText>
                    </FormControl>
                  </LocalizationProvider>
                );
              }
              return null;
            })}
            <Box sx={{ mb: 2 }}>
              <TagsAutocompleteFilterField
                label="Tags"
                value={parseRegistryTagIds(filterState.tagIds)}
                onChange={(ids) =>
                  setFilterState((prev) => ({
                    ...prev,
                    tagIds: formatRegistryTagIds(ids),
                  }))
                }
                options={tagList}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextFilterField
                label="Search in Notes"
                value={filterState.notesQuery || ""}
                onChange={(v) => handleFilterChange("notesQuery", v)}
                placeholder="Search text within notes"
              />
            </Box>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <FormControl sx={InputWrapper}>
                <FormLabel sx={LabelStyle}>
                  Screener Completion Date From
                </FormLabel>
                <DatePicker
                  maxDate={DateTime.now()}
                  value={
                    filterState.screenerCompletedFrom
                      ? DateTime.fromISO(filterState.screenerCompletedFrom)
                      : null
                  }
                  onChange={(value) =>
                    handleFilterChange(
                      "screenerCompletedFrom",
                      value ? value.toISODate() || "" : "",
                    )
                  }
                  slotProps={{
                    field: {
                      clearable: true,
                      onClear: () =>
                        handleFilterChange("screenerCompletedFrom", ""),
                    },
                  }}
                />
                <FormHelperText> </FormHelperText>
              </FormControl>
              <FormControl sx={InputWrapper} error={!!dateError}>
                <FormLabel sx={LabelStyle}>
                  Screener Completion Date To
                </FormLabel>
                <DatePicker
                  maxDate={DateTime.now()}
                  value={
                    filterState.screenerCompletedTo
                      ? DateTime.fromISO(filterState.screenerCompletedTo)
                      : null
                  }
                  onChange={(value) =>
                    handleFilterChange(
                      "screenerCompletedTo",
                      value ? value.toISODate() || "" : "",
                    )
                  }
                  slotProps={{
                    field: {
                      clearable: true,
                      onClear: () =>
                        handleFilterChange("screenerCompletedTo", ""),
                    },
                  }}
                />
                <FormHelperText>{dateError || " "}</FormHelperText>
              </FormControl>
            </LocalizationProvider>
          </Box>
          <Box sx={{ mt: "auto" }}>
            <Divider />
            <Box sx={{ ...ModalActionButtonStyles, mt: 2, mb: 2 }}>
              <Button onClick={handleClearAll} variant="outlined">
                Clear All
              </Button>
              <Button onClick={closeModal} variant="outlined">
                Cancel
              </Button>
              <Button onClick={submit} variant="contained">
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default GlobalFilterModal;
